// Welcome Screen Component for kiosk interface
import React from 'react';
import { MessageSquare, ArrowRight, Settings } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { LanguageSelector } from '../common/LanguageSelector';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { useNavigate } from 'react-router-dom';

interface WelcomeScreenProps {
  className?: string;
  onSetupComplete?: () => void;
}

export const WelcomeScreen: React.FC<WelcomeScreenProps> = ({
  className = '',
  onSetupComplete
}) => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const handleGetStarted = () => {
    navigate('/auth');
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br from-kiosk-background via-kiosk-background to-kiosk-primary/5 ${className}`}>
      {/* Header with Language Selector */}
      <div className="absolute top-4 right-4 z-10">
        <LanguageSelector size="sm" showLabel={false} />
      </div>

      <div className="container mx-auto px-4 py-8 min-h-screen flex items-center justify-center">
        <div className="w-full max-w-4xl">
          {/* Welcome Content */}
          <div className="text-center space-y-8">
            {/* Logo/Icon */}
            <div className="flex justify-center">
              <div className="w-24 h-24 bg-kiosk-primary rounded-full flex items-center justify-center shadow-lg">
                <MessageSquare className="w-12 h-12 text-white" />
              </div>
            </div>

            {/* Welcome Content */}
            <div className="space-y-4">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-kiosk-primary">
                {t('welcome.title')}
              </h1>
              <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold text-kiosk-secondary">
                {t('welcome.subtitle')}
              </h2>
              <p className="text-lg md:text-xl text-kiosk-muted max-w-3xl mx-auto leading-relaxed">
                {t('welcome.description')}
              </p>
            </div>

            {/* Features Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
              <Card className="kiosk-card hover:shadow-lg transition-shadow">
                <CardContent className="p-6 text-center">
                  <MessageSquare className="w-12 h-12 text-kiosk-primary mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-kiosk-base mb-2">
                    Hỏi đáp thông minh
                  </h3>
                  <p className="text-kiosk-muted">
                    Hệ thống AI hỗ trợ trả lời các câu hỏi về thủ tục hành chính
                  </p>
                </CardContent>
              </Card>

              <Card className="kiosk-card hover:shadow-lg transition-shadow">
                <CardContent className="p-6 text-center">
                  <Settings className="w-12 h-12 text-kiosk-primary mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-kiosk-base mb-2">
                    Dễ sử dụng
                  </h3>
                  <p className="text-kiosk-muted">
                    Giao diện thân thiện, tối ưu cho màn hình cảm ứng
                  </p>
                </CardContent>
              </Card>

              <Card className="kiosk-card hover:shadow-lg transition-shadow">
                <CardContent className="p-6 text-center">
                  <ArrowRight className="w-12 h-12 text-kiosk-primary mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-kiosk-base mb-2">
                    Nhanh chóng
                  </h3>
                  <p className="text-kiosk-muted">
                    Nhận câu trả lời chính xác trong vài giây
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Get Started Button */}
            <div className="mt-12">
              <Button
                onClick={handleGetStarted}
                size="lg"
                className="h-16 px-12 text-xl font-semibold bg-kiosk-primary hover:bg-kiosk-primary/90 text-white shadow-lg hover:shadow-xl transition-all min-w-[200px] touch-manipulation"
              >
                {t('welcome.getStarted')}
                <ArrowRight className="w-6 h-6 ml-3" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
        <p className="text-sm text-kiosk-muted text-center">
          Hệ thống hỗ trợ thủ tục hành chính • Phiên bản 1.0
        </p>
      </div>
    </div>
  );
};